import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from "@tanstack/react-query";
import { blipService } from "../services/blipService";
import { BlipV2, BlipsResponse } from "@/types/home";

interface BlipResponse {
  blips: BlipV2[];
  currentPage: number;
  totalPages: number;
  totalBlips: number;
}

// Query for a specific blip by product ID and blip ID
export const useBlipById = (
  productId: string | null,
  blipId: string | null
) => {
  return useQuery<BlipV2 | null>({
    queryKey: ["blip", productId, blipId],
    queryFn: async () => {
      if (!productId || !blipId) return null;
      return await blipService.getBlipById(productId, blipId);
    },
    enabled: !!productId && !!blipId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
};

// Infinite query for blips feed
export const useInfiniteBlips = (limit: number = 10) => {
  return useInfiniteQuery<BlipResponse>({
    queryKey: ["blips", "infinite"],
    queryFn: ({ pageParam = 1 }) =>
      blipService.getProductBlips(pageParam as number, limit),
    getNextPageParam: (lastPage) => {
      return lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined;
    },
    initialPageParam: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// Mutation for liking a blip with optimistic updates
export const useLikeBlip = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      productId,
      blipId,
    }: {
      productId: string;
      blipId: string;
    }) => {
      // Call the like API endpoint with both productId and blipId
      return await blipService.likeBlip(productId, blipId);
    },
    onMutate: async ({ productId, blipId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["blips", "infinite"] });
      await queryClient.cancelQueries({ queryKey: ["blip", productId, blipId] });

      // Snapshot previous values
      const previousBlips = queryClient.getQueryData(["blips", "infinite"]);
      const previousSingleBlip = queryClient.getQueryData(["blip", productId, blipId]);

      // Optimistically update infinite blips cache
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["blips", "infinite"], (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: BlipResponse) => ({
            ...page,
            blips: page.blips.map((blip: BlipV2) =>
              blip.blipId === blipId
                ? {
                    ...blip,
                    blipEngagement: {
                      ...blip.blipEngagement,
                      isLiked: !blip.blipEngagement?.isLiked,
                      likesCount: blip.blipEngagement?.isLiked
                        ? (blip.blipEngagement.likesCount || 0) - 1
                        : (blip.blipEngagement?.likesCount || 0) + 1,
                    },
                  }
                : blip
            ),
          })),
        };
      });

      // Optimistically update single blip cache
      queryClient.setQueryData(["blip", productId, blipId], (old: BlipV2 | null | undefined) => {
        if (!old) return old;

        return {
          ...old,
          blipEngagement: {
            ...old.blipEngagement,
            isLiked: !old.blipEngagement?.isLiked,
            likesCount: old.blipEngagement?.isLiked
              ? (old.blipEngagement.likesCount || 0) - 1
              : (old.blipEngagement?.likesCount || 0) + 1,
          },
        };
      });

      return { previousBlips, previousSingleBlip };
    },
    onError: (err, { productId, blipId }, context) => {
      // Rollback on error
      if (context?.previousBlips) {
        queryClient.setQueryData(["blips", "infinite"], context.previousBlips);
      }
      if (context?.previousSingleBlip) {
        queryClient.setQueryData(["blip", productId, blipId], context.previousSingleBlip);
      }
      console.error("Failed to like blip:", err);
    },
    onSettled: () => {
      // Optional: Refetch to ensure consistency
      // queryClient.invalidateQueries({ queryKey: ['blips', 'infinite'] });
    },
  });
};

// Mutation for following a seller with optimistic updates
export const useFollowSeller = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sellerId: string) => {
      return await blipService.followSeller(sellerId);
    },
    onMutate: async (sellerId) => {
      // Cancel outgoing refetches for blips and suggested sellers
      await queryClient.cancelQueries({ queryKey: ["blips", "infinite"] });
      await queryClient.cancelQueries({ queryKey: ["suggested", "sellers"] });
      // Cancel all individual blip queries
      await queryClient.cancelQueries({ queryKey: ["blip"] });

      // Snapshot previous values
      const previousBlips = queryClient.getQueryData(["blips", "infinite"]);
      const previousSellers = queryClient.getQueryData(["suggested", "sellers"]);

      // Optimistically update infinite blips cache
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["blips", "infinite"], (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: BlipResponse) => ({
            ...page,
            blips: page.blips.map((blip: BlipV2) =>
              blip.seller?._id === sellerId
                ? {
                    ...blip,
                    seller: {
                      ...blip.seller,
                      isFollowing: true,
                    },
                  }
                : blip
            ),
          })),
        };
      });

      // Optimistically update ALL individual blip queries for this seller
      queryClient.setQueriesData({ queryKey: ["blip"] }, (old: BlipV2 | null | undefined) => {
        if (!old || old.seller?._id !== sellerId) return old;

        return {
          ...old,
          seller: {
            ...old.seller,
            isFollowing: true,
          },
        };
      });

      // Optimistically update suggested sellers cache
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueriesData({ queryKey: ["suggested", "sellers"] }, (old: any) => {
        if (!old || !old.sellers) return old;

        return {
          ...old,
          sellers: old.sellers.map((seller: any) =>
            seller._id === sellerId
              ? { ...seller, isFollowing: true }
              : seller
          ),
        };
      });

      return { previousBlips, previousSellers };
    },
    onError: (err, sellerId, context) => {
      // Rollback on error
      if (context?.previousBlips) {
        queryClient.setQueryData(["blips", "infinite"], context.previousBlips);
      }
      if (context?.previousSellers) {
        queryClient.setQueryData(["suggested", "sellers"], context.previousSellers);
      }
      // Invalidate individual blip queries to refetch and get correct state
      queryClient.invalidateQueries({ queryKey: ["blip"] });
      console.error("Failed to follow seller:", err);
    },
    onSettled: () => {
      // Optional: Refetch to ensure consistency
      // queryClient.invalidateQueries({ queryKey: ['blips', 'infinite'] });
      // queryClient.invalidateQueries({ queryKey: ['suggested', 'sellers'] });
    },
  });
};

// Mutation for unfollowing a seller with optimistic updates
export const useUnfollowSeller = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sellerId: string) => {
      return await blipService.unFollowSeller(sellerId);
    },
    onMutate: async (sellerId) => {
      // Cancel outgoing refetches for blips and suggested sellers
      await queryClient.cancelQueries({ queryKey: ["blips", "infinite"] });
      await queryClient.cancelQueries({ queryKey: ["suggested", "sellers"] });
      // Cancel all individual blip queries
      await queryClient.cancelQueries({ queryKey: ["blip"] });

      // Snapshot previous values
      const previousBlips = queryClient.getQueryData(["blips", "infinite"]);
      const previousSellers = queryClient.getQueryData(["suggested", "sellers"]);

      // Optimistically update infinite blips cache
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueryData(["blips", "infinite"], (old: any) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page: BlipResponse) => ({
            ...page,
            blips: page.blips.map((blip: BlipV2) =>
              blip.seller?._id === sellerId
                ? {
                    ...blip,
                    seller: {
                      ...blip.seller,
                      isFollowing: false,
                    },
                  }
                : blip
            ),
          })),
        };
      });

      // Optimistically update ALL individual blip queries for this seller
      queryClient.setQueriesData({ queryKey: ["blip"] }, (old: BlipV2 | null | undefined) => {
        if (!old || old.seller?._id !== sellerId) return old;

        return {
          ...old,
          seller: {
            ...old.seller,
            isFollowing: false,
          },
        };
      });

      // Optimistically update suggested sellers cache
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryClient.setQueriesData({ queryKey: ["suggested", "sellers"] }, (old: any) => {
        if (!old || !old.sellers) return old;

        return {
          ...old,
          sellers: old.sellers.map((seller: any) =>
            seller._id === sellerId
              ? { ...seller, isFollowing: false }
              : seller
          ),
        };
      });

      return { previousBlips, previousSellers };
    },
    onError: (err, sellerId, context) => {
      // Rollback on error
      if (context?.previousBlips) {
        queryClient.setQueryData(["blips", "infinite"], context.previousBlips);
      }
      if (context?.previousSellers) {
        queryClient.setQueryData(["suggested", "sellers"], context.previousSellers);
      }
      // Invalidate individual blip queries to refetch and get correct state
      queryClient.invalidateQueries({ queryKey: ["blip"] });
      console.error("Failed to unfollow seller:", err);
    },
    onSettled: () => {
      // Optional: Refetch to ensure consistency
      // queryClient.invalidateQueries({ queryKey: ['blips', 'infinite'] });
      // queryClient.invalidateQueries({ queryKey: ['suggested', 'sellers'] });
    },
  });
};

// Query to check if a blip is saved
export const useCheckBlipSaved = (blipId: string | null | undefined) => {
  return useQuery<boolean>({
    queryKey: ["blip", "saved", blipId],
    queryFn: async () => {
      if (!blipId) return false;
      const response = await blipService.checkBlipSaved(blipId);
      return response.isSaved;
    },
    enabled: !!blipId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
};

// Mutation for saving/unsaving a blip with optimistic updates
export const useSaveBlip = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { status: boolean; message: string; created?: boolean; removed?: boolean },
    Error,
    { blipId: string; isCurrentlySaved: boolean }
  >({
    mutationFn: async ({ blipId, isCurrentlySaved }) => {
      if (isCurrentlySaved) {
        // Unsave the blip
        return await blipService.unsaveBlip(blipId);
      } else {
        // Save the blip
        return await blipService.saveBlip(blipId);
      }
    },
    onSuccess: (data, variables) => {
      // Update the saved state query cache
      queryClient.setQueryData(
        ["blip", "saved", variables.blipId],
        !variables.isCurrentlySaved
      );
      // Invalidate saved blips list to refresh it
      queryClient.invalidateQueries({ queryKey: ["blips", "saved"] });
    },
    onError: (error, variables) => {
      // Revert the saved state query cache on error
      queryClient.setQueryData(
        ["blip", "saved", variables.blipId],
        variables.isCurrentlySaved
      );
    },
  });
};

// Query for fetching saved blips with pagination
export const useSavedBlips = (page: number = 1, limit: number = 20) => {
  return useQuery<BlipsResponse>({
    queryKey: ["blips", "saved", page, limit],
    queryFn: () => blipService.getSavedBlips(page, limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
};